<template>
  <div class="card mb-4">
    <div class="card-header">
      <div class="row flex-between-center">
        <div class="col-auto d-flex align-items-center pe-0">
          <h5 class="fs-6 mb-0 text-nowrap py-2 py-xl-0">Using the API</h5>
        </div>
      </div>
    </div>

    <div class="card-body bg-body-tertiary">

      <p class="mb-2">
        Your API keys let your own scripts, bots and integrations talk to the Signum API directly - read your bots and market data, manage bots, and send trading signals.
      </p>

      <p class="mb-2">
        Send every request to <code>{{ apiBase }}</code> and authenticate with your key as a Bearer token:
      </p>

      <CodeBlock class="mb-3" code="Authorization: Bearer sna_your_key_here" />

      <p class="mb-3 fs-10 text-700">
        Each call needs the matching permission on your key:
        <span class="badge rounded-pill badge-subtle-secondary">read</span> to fetch,
        <span class="badge rounded-pill badge-subtle-info">write</span> to create or change bots,
        <span class="badge rounded-pill badge-subtle-warning">trading</span> to send signals.
        The examples below assume <code>SIGNUM_API_KEY</code> holds your key.
      </p>

      <details v-for="section in sections" :key="section.title" class="docs-section">
        <summary class="docs-summary">
          <span class="fw-bold">{{ section.title }}</span>
          <span class="text-700 fs-10 ms-2">{{ section.subtitle }}</span>
        </summary>

        <div class="docs-section-body">
          <div v-for="item in section.items" :key="item.method + item.path" class="endpoint mb-3 pb-3">
            <div class="d-flex align-items-center flex-wrap gap-2 mb-1">
              <span class="badge rounded-pill" :class="methodClass(item.method)">{{ item.method }}</span>
              <code class="endpoint-path">{{ item.path }}</code>
              <span class="badge rounded-pill" :class="permClass(item.permission)">{{ item.permission }}</span>
            </div>
            <p class="mb-2 fs-10">{{ item.summary }}</p>

            <div class="code-label">Request</div>
            <CodeBlock :code="curl(item)" />

            <details v-if="item.response !== undefined" class="response-details mt-2">
              <summary class="code-label response-summary">Response</summary>
              <CodeBlock :code="responseText(item)" /> 
            </details>

            <p v-if="item.note" class="mb-0 mt-2 fs-10 text-warning">
              <span class="fas fa-info-circle me-1"></span>{{ item.note }}
            </p>
          </div>
        </div>
      </details>

      <p class="fs-10 text-700 mb-0 mt-0 pt-3 border-top">
        <span class="fas fa-info-circle me-0"></span> NOTICE: The Signum API is still evolving, so endpoints and response fields may change at any time without notice 🙏🏼
      </p>

    </div>
  </div>
</template>

<script>
import CodeBlock from '@/components/CodeBlock'

// Representative bot object as returned by the API (secrets like apiSecret are
// never included in responses). Reused across the bot read/write examples so
// they all show the same shape.
const BOT_EXAMPLE = {
  id: 19,
  publicCode: 'tZ4kQ9mB',
  title: 'My BTC bot',
  enabled: true,
  tradingAccount: 'spot',
  leverage: 1,
  throttledStatus: 'none',
  exchangeId: 'bybit',
  baseCoin: 'BTC',
  quoteCoin: 'USDT',
  ipAllowedTradingView: true,
  ipAllowedMake: false,
  ipAllowedZapier: false,
  ipAllowedCustomList: '',
  category: null,
  createdAt: '2026-06-09T18:54:02+00:00',
  updatedAt: '2026-06-14T10:12:34+00:00',
  exchangeConfig: { apiKey: 'AbC123...', isTestNet: false },
  webhookDetailsBasic: {
    alertName: 'My BTC bot (SIGNUM -> Bybit)',
    url: 'https://api.signum.money/trading',
    message: '{"action":"{{strategy.order.action}}","ticker":"{{ticker}}","order_size":"100%","position_size":"{{strategy.position_size}}","schema":"2","bot_id":"tZ4kQ9mB"}'
  },
  webhookDetailsAdvanced: {
    alertName: 'My BTC bot (SIGNUM -> Bybit)',
    url: 'https://api.signum.money/trading',
    message: '{"action":"{{strategy.order.action}}","ticker":"{{ticker}}","order_size":"{{strategy.order.contracts}}","position_size":"{{strategy.position_size}}","schema":"2","bot_id":"tZ4kQ9mB"}'
  }
}

// Sentinel placed as the last element of an example array. responseText()
// renders it as a `{ // one object per X }` marker showing the list continues.
const more = (label) => ({ __more: label })

export default {
  components: {
    CodeBlock
  },
  computed: {
    apiBase () {
      return process.env.VUE_APP_API_URL
    },
    sections () {
      return [
        {
          title: 'Bots',
          subtitle: 'read & manage your trading bots',
          items: [
            {
              method: 'GET', path: '/bots', permission: 'read',
              summary: 'List all of your bots.',
              response: [BOT_EXAMPLE, more('one object per bot')]
            },
            {
              method: 'GET', path: '/bots/{id}', permission: 'read',
              summary: 'Get a single bot by its numeric id.',
              response: BOT_EXAMPLE
            },
            {
              method: 'GET', path: '/auditlog/bot/{id}?page=1', permission: 'read',
              summary: 'Read a bot\'s activity log: received signals, placed orders, warnings and errors. logLevel is one of info, warning, error. explainer is a human-readable explanation (null when there is none).',
              response: {
                items: [
                  { explainer: 'Signum successfully placed the order on your exchange.', id: 84218, userAccountId: 42, botId: 19, timestamp: '2026-06-14T10:12:35+00:00', logLevel: 'info', message: 'order performed successfully on Bybit, bot_id: tZ4kQ9mB, apiKey: ..., action: sell, pair: BTC/USDT, amount: 0.0123, funding_amount: 0.01230000000000000000, leverage: 1, order ID: 1932274601' },
                  { explainer: 'Signum has received a new trading signal.', id: 84213, userAccountId: 42, botId: 19, timestamp: '2026-06-14T10:12:34+00:00', logLevel: 'info', message: 'identified bot for incoming trading signal, bot_id: tZ4kQ9mB, ticker: BTCUSDT, action: sell, order_size: 100%, position_size: 0, schema: 2, timestamp: 2026-06-14T10:12:34+00:00' },
                  { explainer: 'You are sending trading signals for a bot that you have paused. Either activate the bot or stop sending those signals.', id: 84190, userAccountId: 42, botId: 19, timestamp: '2026-06-14T09:58:01+00:00', logLevel: 'warning', message: 'skipped sell signal for disabled bot, bot_id: tZ4kQ9mB' },
                  { explainer: 'Your exchange would not execute your order with the funding amount you have available. Either your strategy is out-of-sync with the funds you have, then ignore this message and expect your next order to work. Or you don\'t have enough funds, then deposit funds to your account.', id: 84155, userAccountId: 42, botId: 19, timestamp: '2026-06-14T09:40:12+00:00', logLevel: 'error', message: 'error placing order on Bybit, bot_id: tZ4kQ9mB, apiKey: ..., action: sell, pair: BTC/USDT, amount: 0.00001319, funding_amount: 0.0000132000, price: 0, leverage: 1, side: base, message: Bybit API request failed: bybit {"retCode":170131,"retMsg":"Insufficient balance."}' },
                  more('one object per log entry')
                ],
                page: 1,
                pages: 5,
                limit: 10,
                results: 47
              }
            },
            {
              method: 'POST', path: '/bots', permission: 'write',
              summary: 'Create a bot. exchangeConfig fields depend on the exchange - call GET /exchanges to see what is supported.',
              body: {
                title: 'My BTC bot',
                enabled: true,
                exchangeId: 'bybit',
                tradingAccount: 'spot',
                leverage: 1,
                exchangeConfig: { apiKey: '...', apiSecret: '...', isTestNet: false },
                baseCoin: 'BTC',
                quoteCoin: 'USDT'
              },
              response: BOT_EXAMPLE
            },
            {
              method: 'PATCH', path: '/bots/{id}', permission: 'write',
              summary: 'Update a bot. Send only the fields you want to change.',
              body: { title: 'Renamed bot', enabled: false },
              response: { ...BOT_EXAMPLE, title: 'Renamed bot', enabled: false }
            },
            {
              method: 'DELETE', path: '/bots/{id}', permission: 'write',
              summary: 'Delete a bot permanently.',
              response: { result: 'deleted' }
            }
          ]
        },
        {
          title: 'Exchange',
          subtitle: 'exchanges, balances, positions & tradable pairs',
          items: [
            {
              method: 'GET', path: '/exchanges', permission: 'read',
              summary: 'List supported exchanges and which account types (spot, margin, perpetual) they offer.',
              response: [
                { id: 'bybit', name: 'Bybit', features: { tradingAccount: { spot: true, margin: false, perpetual: true } } },
                { id: 'hyperliquid', name: 'Hyperliquid', features: { tradingAccount: { spot: true, margin: false, perpetual: true } } },
                more('one object per exchange')
              ]
            },
            {
              method: 'GET', path: '/exchange/assets/{exchangeId}?botId={id}', permission: 'read',
              summary: 'Fetch a bot\'s live balances (spot) and open positions (futures) from its exchange. {exchangeId} is the bot\'s exchangeId; pass the bot\'s id as botId.',
              response: {
                balance: [
                  { coin: 'BTC', balance: '0.0123000000', visibleBalance: '0.0123000000' },
                  { coin: 'USDT', balance: '250.5000000000', visibleBalance: '250.5000000000' },
                  more('one object per coin balance')
                ],
                positions: [
                  { baseCoin: 'ETH', quoteCoin: 'USDT', direction: 'long', leverage: 5, sizeAsBaseCoin: 0.187, currentCollateralAsQuoteCoin: 100, unrealizedPnlAsQuoteCoin: 12.34, unrealizedPnlAsPercentage: 1.03 },
                  { baseCoin: 'BTC', quoteCoin: 'USDT', direction: 'short', leverage: 3, sizeAsBaseCoin: 0.005, currentCollateralAsQuoteCoin: 200, unrealizedPnlAsQuoteCoin: -8.5, unrealizedPnlAsPercentage: -4.25 },
                  more('one object per open position')
                ]
              },
              note: 'balance lists spot holdings (balance is available, visibleBalance is total equity); positions lists open futures positions (empty for spot bots).'
            },
            {
              method: 'GET', path: '/exchange/pairs/{exchangeId}', permission: 'read',
              summary: 'List the trading pairs a bot can trade on its exchange. Besides the required botId, you can filter and sort over symbol, baseCoin, quoteCoin and maxLeverage. filter joins conditions with & (operators: = != > >= < <=; a comma-separated value is an IN list); sort is comma-separated field:asc|desc.',
              query: [
                { name: 'botId', value: '{id}' },
                { name: 'filter', value: 'quoteCoin=USDT,USDC&maxLeverage>=10&baseCoin!=BTC' },
                { name: 'sort', value: 'maxLeverage:desc,symbol:asc' }
              ],
              response: [
                { symbol: 'ETH/USDT', baseCoin: 'ETH', quoteCoin: 'USDT', status: 'active', maxLeverage: 25 },
                { symbol: 'SOL/USDC', baseCoin: 'SOL', quoteCoin: 'USDC', status: 'active', maxLeverage: 20 },
                more('one object per tradable pair')
              ],
              note: 'maxLeverage is 1 for spot pairs; on perpetual exchanges it reflects the pair\'s max leverage.'
            }
          ]
        },
        {
          title: 'Trading signals',
          subtitle: 'open, close and flip positions',
          items: [
            {
              method: 'POST', path: '/signals', permission: 'trading',
              summary: 'Send a trading signal to one of your bots.',
              body: {
                bot_id: 'tZ4kQ9mB',
                ticker: 'BTCUSDT',
                action: 'buy',
                order_size: '100%',
                position_size: '1',
                schema: '2'
              },
              response: { result: 'queued' },
              note: 'bot_id is the bot\'s publicCode (from GET /bots), not its numeric id. The "queued" response means the signal was accepted for async processing - re-fetch the bot\'s log to confirm execution.'
            }
          ]
        },
        {
          title: 'Trend Radar',
          subtitle: 'daily scans & historic trend counts',
          items: [
            {
              method: 'GET', path: '/trendradar/daily?detector=gc&assetClass=crypto', permission: 'read',
              summary: 'Latest Trend Radar scan results. detector e.g. gc (Gaussian Channel); assetClass e.g. crypto. Add &runDate=YYYY-MM-DD for a specific day.',
              response: [
                {
                  id: 7904, runDate: '2026-06-09', assetClass: 'crypto', detector: 'gc',
                  symbolTitle: 'Ethereum Classic', symbol: 'BINANCE:ETCUSDT',
                  trendFrom: 'Green', trendTo: 'Red', trendChanged: '2026-06-08',
                  currentStatus: 'CLOSED', tradeDate: '2026-06-08', pnlPct: null,
                  breakoutDate: '2026-04-23', breakoutResistance: 8.4, breakoutSupport: 7.76,
                  breakoutInvalidatedAt: null, breakoutValidatedAt: '2026-05-10',
                  marketCap: 1102140578, marketRank: 53,
                  changed: '2026-06-09T14:10:30.561308',
                  breakoutChartUrl: '/trendradar/breakout-chart/7904.png?s=...',
                  indicators: {
                    v: 2,
                    bo: { support: 7.76, resistance: 8.4, breakoutDate: '2026-04-23', supportLineStart: '2026-02-05', resistanceLineStart: '2026-03-25' },
                    tz: 'UTC',
                    data: [
                      { date: '2026-06-06', ohlc: { o: 6.81, h: 6.88, l: 6.42, c: 6.79 }, volume: 314776.75, gc: { lower: 8.20309846, upper: 9.33723918, filter: 8.77016882, trend: 'Green' } },
                      more('one entry per candle')
                    ],
                    date: '2026-06-08',
                    detector: 'gc',
                    cmcRefPriceUsd: 7.01843538
                  }
                },
                more('one object per asset')
              ]
            },
            {
              method: 'GET', path: '/trendradar/historic?detector=gc&assetClass=crypto&days=20', permission: 'read',
              summary: 'Historic daily counts of bullish / neutral / bearish symbols over the last N days.',
              response: [
                { runDate: '2026-06-09', lastUpdated: '2026-06-09T14:10:30+00:00', assetClass: 'crypto', detector: 'gc', green: '55', grey: '0', red: '45', total: 100 },
                { runDate: '2026-06-08', lastUpdated: '2026-06-08T14:10:30+00:00', assetClass: 'crypto', detector: 'gc', green: '54', grey: '1', red: '45', total: 100 },
                more('one object per day')
              ]
            }
          ]
        }
      ]
    }
  },
  methods: {
    methodClass (method) {
      switch (method) {
        case 'GET': return 'badge-subtle-success'
        case 'POST': return 'badge-subtle-primary'
        case 'PATCH': return 'badge-subtle-warning'
        case 'DELETE': return 'badge-subtle-danger'
        default: return 'badge-subtle-secondary'
      }
    },
    permClass (permission) {
      switch (permission) {
        case 'read': return 'badge-subtle-secondary'
        case 'write': return 'badge-subtle-info'
        case 'trading': return 'badge-subtle-warning'
        default: return 'badge-subtle-secondary'
      }
    },
    curl (item) {
      const url = `${this.apiBase}${item.path}`
      if (item.body) {
        return `curl -X ${item.method} "${url}" \\\n  -H "Authorization: Bearer $SIGNUM_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(item.body, null, 2)}'`
      }
      if (item.query) {
        // -G + --data-urlencode keeps each value readable while letting curl
        // URL-encode it correctly (e.g. the & joining filter conditions).
        const params = item.query.map(q => ` \\\n  --data-urlencode "${q.name}=${q.value}"`).join('')
        return `curl -G "${url}" \\\n  -H "Authorization: Bearer $SIGNUM_API_KEY"${params}`
      }
      return `curl "${url}" \\\n  -H "Authorization: Bearer $SIGNUM_API_KEY"`
    },
    responseText (item) {
      // Render each `more('one object per X')` sentinel as a { // one object per X }
      // marker, so every example array clearly shows the list continues.
      return JSON.stringify(item.response, null, 2).replace(/"__more": "([^"]+)"/g, '// $1')
    }
  }
}
</script>

<style scoped>
.code-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--falcon-secondary-color);
  margin-bottom: 0.25rem;
}
.response-summary {
  cursor: pointer;
  user-select: none;
  list-style: none;
  display: inline-flex;
  align-items: center;
}
.response-summary::-webkit-details-marker {
  display: none;
}
.response-summary::before {
  content: '\25B8'; /* right-pointing triangle */
  display: inline-block;
  margin-right: 0.45rem;
  font-size: 1.05rem;
  line-height: 1;
  transition: transform 0.15s ease;
}
.response-details[open] > .response-summary {
  margin-bottom: 0.35rem;
}
.response-details[open] > .response-summary::before {
  transform: rotate(90deg);
}
.endpoint-path {
  font-size: 0.85rem;
}
.endpoint:not(:last-child) {
  border-bottom: 1px solid var(--falcon-border-color);
}
.docs-section {
  border-top: 1px solid var(--falcon-border-color);
  padding: 0.5rem 0;
}
.docs-summary {
  cursor: pointer;
  list-style: none;
  padding: 0.5rem 0;
  display: flex;
  align-items: center;
}
.docs-summary::-webkit-details-marker {
  display: none;
}
.docs-summary::before {
  content: '\25B8'; /* right-pointing triangle */
  display: inline-block;
  margin-right: 0.5rem;
  font-size: 1.3rem;
  line-height: 1;
  transition: transform 0.15s ease;
  color: var(--falcon-secondary-color);
}
.docs-section[open] > .docs-summary::before {
  transform: rotate(90deg);
}
.docs-section-body {
  padding: 0.5rem 0 0 1.25rem;
}
</style>
